/**
 * Scoring system — Stage 1c.
 *
 * Pure scoring state machine per TUNING.score. Owns:
 *  - per-metre distance accumulation (`distanceScore += perMeter * metres`),
 *  - kick / near-miss bonuses BANKED into `bonusScore` at award time, scaled by
 *    the multiplier in force at that moment (`bonusScore += award * multiplier`,
 *    multiplier bumped after the award, so the first action pays x1),
 *  - the combo multiplier: +1 per kick/near-miss up to `multiplierMax`,
 *    decaying continuously at -1/s after `multiplierDecayAfter` seconds of
 *    inaction (floored at 1),
 *  - `total = floor(distanceScore + bonusScore)`,
 *
 * Banking at award time is what makes the total MONOTONICALLY NON-DECREASING.
 * The old formula multiplied the whole accumulated bonus by the LIVE
 * multiplier, so the on-screen score counted down while the multiplier decayed
 * (up to 16x of the banked bonus evaporating for doing nothing wrong) and the
 * final score depended on whether the crash happened to land within the 5 s
 * combo grace window.
 *  - kick / near-miss counters.
 *
 * Determinism: scoring is a pure function of (state, events, time, dt). The
 * decay is tracked exactly via an internal `decayApplied` accumulator so the
 * multiplier only decreases for time actually elapsed beyond the grace
 * window — no double-counting, no partial-step drift, identical across runs.
 *
 * Zero per-frame allocation: the Scoring object holds one ScoreState (mutated
 * in place) plus a couple of scalar fields; no arrays, no closures per call.
 */

import { TUNING } from "./tuning";
import type { ScoreState, VehicleKind } from "./types";

const S = TUNING.score;

/** Minimum multiplier (the floor decay never crosses). */
const MIN_MULTIPLIER = 1;

/**
 * Mutable scoring controller. The public projection is `state` (a ScoreState
 * the world embeds in its WorldState); internal bookkeeping for the decay
 * timer lives alongside it.
 */
export interface Scoring {
  readonly state: ScoreState;
  /** Add `metres` of travel to the distance score. */
  addDistance(metres: number): void;
  /** Record a kick of `kind` at sim `time`; awards bonus, bumps multiplier, resets decay. */
  recordKick(kind: VehicleKind, time: number): void;
  /** Record a near-miss at sim `time`; awards bonus, bumps multiplier, resets decay. */
  recordNearMiss(time: number): void;
  /**
   * Record ONE coin pickup worth `value` (sim/coins.ts).
   *
   * Deliberately NOT a combo action: the value is banked FLAT, the multiplier
   * is neither applied nor bumped, and the decay timer is NOT reset. A chain is
   * 6-10 pickups inside two seconds — running it through the combo economy
   * would pin the multiplier at its cap for free and drown the kick/near-miss
   * awards it exists to reward. `time` is accepted for symmetry with the other
   * recorders (and so a future coin-combo can be added without a signature
   * change); it is unused today.
   */
  recordCoin(value: number, time: number): void;
  /** Advance the decay timer given current sim `time` and step `dt`. */
  tickDecay(time: number, dt: number): void;
  /** Recompute `total` from the current sub-scores (call before reading total). */
  recomputeTotal(): void;
  /** Reset to a fresh run state. */
  reset(): void;
}
export function createScoring(): Scoring {
  const state: ScoreState = {
    distanceScore: 0,
    bonusScore: 0,
    multiplier: MIN_MULTIPLIER,
    lastActionTime: 0,
    total: 0,
    kicks: 0,
    nearMisses: 0,
    coins: 0,
  };

  // Internal: how much decay time has already been consumed (seconds). Reset
  // to 0 on every action so decay only counts elapsed time beyond the grace
  // window since the last action.
  let decayApplied = 0;

  function bumpMultiplier(): void {
    if (state.multiplier < S.multiplierMax) {
      state.multiplier += 1;
      if (state.multiplier > S.multiplierMax) state.multiplier = S.multiplierMax;
    }
  }

  function addDistance(metres: number): void {
    state.distanceScore += S.perMeter * metres;
  }

  function recordKick(kind: VehicleKind, time: number): void {
    // Banked at the CURRENT multiplier, before the bump: the first action of a
    // combo pays x1, the second x2, ... and nothing can un-bank it later.
    const award = kind === "truck" ? S.kickTruck : S.kickCar;
    state.bonusScore += award * state.multiplier;
    state.kicks += 1;
    bumpMultiplier();
    state.lastActionTime = time;
    decayApplied = 0;
  }
  function recordNearMiss(time: number): void {
    state.bonusScore += S.nearMiss * state.multiplier;
    state.nearMisses += 1;
    bumpMultiplier();
    state.lastActionTime = time;
    decayApplied = 0;
  }

  function recordCoin(value: number, time: number): void {
    void time; // see the interface doc: coins are outside the combo economy.
    state.bonusScore += value;
    state.coins += 1;
  }

  function tickDecay(time: number, dt: number): void {
    void dt; // dt unused: decay is computed from absolute elapsed time.
    const elapsed = time - state.lastActionTime;
    if (elapsed > S.multiplierDecayAfter && state.multiplier > MIN_MULTIPLIER) {
      const totalDecayTime = elapsed - S.multiplierDecayAfter;
      const newDecay = totalDecayTime - decayApplied;
      if (newDecay > 0) {
        state.multiplier -= newDecay;
        if (state.multiplier < MIN_MULTIPLIER) state.multiplier = MIN_MULTIPLIER;
        decayApplied = totalDecayTime;
      }
    }
  }

  function recomputeTotal(): void {
    state.total = Math.floor(state.distanceScore + state.bonusScore);
  }

  function reset(): void {
    state.distanceScore = 0;
    state.bonusScore = 0;
    state.multiplier = MIN_MULTIPLIER;
    state.lastActionTime = 0;
    state.total = 0;
    state.kicks = 0;
    state.nearMisses = 0;
    state.coins = 0;
    decayApplied = 0;
  }

  return {
    state,
    addDistance,
    recordKick,
    recordNearMiss,
    recordCoin,
    tickDecay,
    recomputeTotal,
    reset,
  };
}
